import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import * as z from 'zod';

const FetchBaseQueryErrorSchema = z.object({
  status: z.union([z.string(), z.number()]),
  data: z
    .object({
      error_code: z.string(),
      detail: z.string(),
    })
    .partial()
    .nullable(),
});

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(
  error: unknown,
): error is FetchBaseQueryError {
  return FetchBaseQueryErrorSchema.safeParse(error).success;
}

export function isFetchBaseQueryTimeoutError(
  error?: object,
): error is Extract<FetchBaseQueryError, { status: 'TIMEOUT_ERROR' }> {
  if (!error) return false;
  return (
    error !== undefined && 'status' in error && error.status === 'TIMEOUT_ERROR'
  );
}

export function isQueryFulfilledRejectionTimeoutError(
  e: unknown,
): e is { error: Extract<FetchBaseQueryError, { status: 'TIMEOUT_ERROR' }> } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'error' in e &&
    typeof e.error === 'object' &&
    e.error !== null &&
    isFetchBaseQueryTimeoutError(e.error)
  );
}

export function isQueryFulfilledRejectionReason<E>(
  e: unknown,
): e is { error: Omit<FetchBaseQueryError, 'data'> & { data: E } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'error' in e &&
    isFetchBaseQueryWithAutoneError(e.error)
  );
}

export function isFetchBaseQueryWithAutoneError<E>(
  error: unknown,
): error is Omit<FetchBaseQueryError, 'data'> & { data: E } {
  return (
    isFetchBaseQueryError(error) &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'error_code' in error.data &&
    'detail' in error.data &&
    'body' in error.data
  );
}
